import {
  Dialog,
  DialogContent,
  IconButton,
  DialogTitle,
  DialogActions,
  useTheme,
  useMediaQuery,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GridContainer, GridItem } from '../shared/ui-extension';
import { ReactNode, useState } from 'react';

interface ModalProps {
  title?: string | ReactNode;
  hideCloseButton?: boolean;
  // Skips the title bar row entirely (no title/titleActions) and floats the
  // close button over the content instead, for modals that don't need a heading.
  hideTitleBar?: boolean;
  children: ReactNode;
  actions?: ReactNode;
  titleActions?: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  dialogContentSx?: object;
  // Opt-in: allow closing on a backdrop click. Off by default so editable
  // forms don't lose changes from an accidental outside click.
  allowBackdropClose?: boolean;
  // Optional warning shown when a blocked backdrop click is ignored, so the
  // user understands why the dialog didn't close and how to close it safely.
  backdropWarningMessage?: string;
}

const Modal = ({
  title,
  hideCloseButton,
  hideTitleBar = false,
  children,
  actions,
  titleActions,
  open,
  setOpen,
  maxWidth = 'lg',
  fullScreen = false,
  dialogContentSx,
  allowBackdropClose = false,
  backdropWarningMessage,
}: ModalProps) => {
  const theme = useTheme();
  const [showBackdropWarning, setShowBackdropWarning] = useState(false);

  // Reset the backdrop warning on every close so a stale warning (e.g. left
  // over from a backdrop click) can't flash when the modal is reopened.
  const closeModal = () => {
    setShowBackdropWarning(false);
    setOpen(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen || useMediaQuery(theme.breakpoints.down('sm'))}
      open={open}
      scroll={'body'}
      onClose={(_event, reason) => {
        // Keep the dialog open on a backdrop click unless the caller opted in
        // (e.g. read-only content where there's nothing to lose).
        if (reason === 'backdropClick' && !allowBackdropClose) {
          if (backdropWarningMessage) setShowBackdropWarning(true);
          return;
        }
        closeModal();
      }}
    >
      {hideTitleBar ? (
        !hideCloseButton && (
          <IconButton
            aria-label='close'
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )
      ) : (
        <>
          <DialogTitle sx={{ m: 0, p: 2 }} id='customized-dialog-title'>
            <GridContainer justifyContent={'space-between'} alignItems='center'>
              <GridItem>{title}</GridItem>
              <GridItem>
                <GridContainer justifyContent='flex-end'>
                  <GridItem>{titleActions}</GridItem>
                  {!hideCloseButton && (
                    <GridItem>
                      <IconButton
                        aria-label='close'
                        onClick={() => {
                          closeModal();
                        }}
                        sx={{
                          color: (theme) => theme.palette.grey[500],
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </GridItem>
                  )}
                </GridContainer>
              </GridItem>
            </GridContainer>
          </DialogTitle>
          <Divider />
        </>
      )}
      <DialogContent sx={dialogContentSx}>
        <GridContainer>{children}</GridContainer>
      </DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
      {backdropWarningMessage && (
        <Snackbar
          open={showBackdropWarning}
          autoHideDuration={5000}
          onClose={() => setShowBackdropWarning(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity='warning'
            onClose={() => setShowBackdropWarning(false)}
          >
            {backdropWarningMessage}
          </Alert>
        </Snackbar>
      )}
    </Dialog>
  );
};

export default Modal;
